import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { checkPageAccess } from '@aidkitorg/types/lib/admin-panel/permissions';
import { canAccessSupportChannel, ChannelConfig } from './Support/utils';
import type { LoggedInConfig } from '../Context';

export type NavItem = Omit<NavigationNode, 'href'> & {
  iconName?: IconName;
  href?: string;
  items?: Array<NavigationNode & { iconName?: IconName; href: string }>
  isExternal?: boolean
};

type ChannelSupportDashboard = { name?: string | Record<string, string>; limitedToTags?: string[] };
type CommsConfig = { default?: ChannelConfig & { supportDashboard?: ChannelSupportDashboard }; otherChannels?: Array<ChannelConfig & { channelName: string; supportDashboard?: ChannelSupportDashboard }> };

export const getNavItems = (L: any, userTags?: string[], loggedInConfig?: LoggedInConfig, options?: { organizationsEnabled?: boolean }): NavItem[] => [
  {
    name: L.admin_panel.nav.home,
    href: '/admin-panel/home',
    iconName: 'HomeIcon'
  },
  ...(checkPageAccess(userTags, ['all-applicants', 'applicant-groups', 'applicant-search'], 'view') ? [{
    name: L.admin_panel.nav.applicants,
    iconName: 'UserCircleIcon' as IconName,
    items: [
      ...(checkPageAccess(userTags, 'all-applicants', 'view') ? [{
        name: L.admin_panel.nav.all_applicants,
        href: '/admin-panel/applicants/all',
      }] : []),
      ...(checkPageAccess(userTags, 'applicant-groups', 'view') ? [{
        name: L.admin_panel.nav.applicant_groups,
        href: '/admin-panel/applicants/applicant-groups',
      }] : []),
      ...(checkPageAccess(userTags, 'applicant-search', 'view') ? [{
        name: L.admin_panel.nav.applicant_search,
        href: '/admin-panel/applicants/applicant-search',
      }] : []),
    ]
  }] : []),
  ...(checkPageAccess(userTags, 'dashboards', 'view') ? [{
    name: L.admin_panel.nav.dashboards,
    href: '/admin-panel/dashboards',
    iconName: 'PresentationChartBarIcon' as IconName
  }] : []),
  ...(checkPageAccess(userTags, 'reports', 'view') ? [{
    name: L.admin_panel.nav.reports,
    href: '/admin-panel/reports',
    iconName: 'TableCellsIcon' as IconName
  }] : []),
  ...(checkPageAccess(userTags, ['my-reviews', 'review-queues'], 'view') ? [{
    name: L.admin_panel.nav.reviews,
    iconName: 'InboxStackIcon' as IconName,
    items: [
      ...(checkPageAccess(userTags, 'my-reviews', 'view') ? [{
        name: L.admin_panel.nav.my_reviews,
        href: '/admin-panel/reviews/my-reviews',
        iconName: 'ClipboardDocumentCheckIcon' as IconName
      }] : []),
      ...(checkPageAccess(userTags, 'review-queues', 'view') ? [{
        name: L.admin_panel.nav.review_queues,
        href: '/admin-panel/reviews/queues',
        iconName: 'QueueListIcon' as IconName
      }] : []),
    ]
  }] : []),
  ...(checkPageAccess(userTags, ['payments', 'balances', 'transactions', 'invoices'], 'view') ? [{
    name: L.admin_panel.nav.accounting,
    iconName: 'CurrencyDollarIcon' as IconName,
    items: [
      ...(checkPageAccess(userTags, 'payments', 'view') ? [{
        name: L.admin_panel.nav.payments,
        href: '/admin-panel/payments',
        iconName: 'CurrencyDollarIcon' as IconName
      }] : []),
      ...(checkPageAccess(userTags, 'balances', 'view') ? [{
        name: L.admin_panel.nav.balances,
        href: '/admin-panel/balances',
        iconName: 'WalletIcon' as IconName
      }] : []),
      ...(checkPageAccess(userTags, 'transactions', 'view') ? [{
        name: L.admin_panel.nav.transactions,
        href: '/admin-panel/transactions',
        iconName: 'CurrencyDollarIcon' as IconName
      }] : []),
      ...(checkPageAccess(userTags, 'invoices', 'view') ? [{
        name: 'Invoices',
        href: '/admin-panel/invoices',
        iconName: 'DocumentTextIcon' as IconName
      }] : []),
    ]
  }] : []),
  ...(checkPageAccess(userTags, 'notifications', 'view') ? [{
    name: L.admin_panel.nav.notifications,
    href: '/admin-panel/notifications',
    iconName: 'BellIcon' as IconName
  }] : []),
  ...((checkPageAccess(userTags, 'users', 'view') || (options?.organizationsEnabled && checkPageAccess(userTags, 'organizations', 'view'))) ? [{
    name: L.admin_panel.nav.user_management,
    iconName: 'UsersIcon' as IconName,
    items: [
      ...(checkPageAccess(userTags, 'users', 'view') ? [{
        name: L.admin_panel.nav.users,
        href: '/admin-panel/user-management/users',
      }] : []),
      ...(checkPageAccess(userTags, 'users', 'view') ? [{
        name: L.admin_panel.user_groups.title,
        href: '/admin-panel/user-management/user-groups',
      }] : []),
      ...(options?.organizationsEnabled && checkPageAccess(userTags, 'organizations', 'view') ? [{
        name: L.admin_panel.nav.organizations,
        href: '/admin-panel/user-management/organizations',
      }] : [])
    ]
  }] : []),
  ...((checkPageAccess(userTags, 'manage-fields', 'view') || checkPageAccess(userTags, 'template-settings', 'view')) ? [{
    name: L.admin_panel.nav.settings,
    iconName: 'CogIcon' as IconName,
    items: [
      ...(checkPageAccess(userTags, 'manage-fields', 'view') ? [{
        name: L.admin_panel.settings.manage_fields,
        href: '/admin-panel/settings/manage-fields',
      }] : []),
      ...(checkPageAccess(userTags, 'template-settings', 'view') ? [{
        name: L.admin_panel.settings.support_template_global_config.title,
        href: '/admin-panel/settings/support-templates',
      }] : []),
    ]
  }] : []),
  ...(checkPageAccess(userTags, 'audit-logs', 'view') ? [{
    name: L.admin_panel.nav.audit_logs,
    href: '/admin-panel/audit-logs',
    iconName: 'DocumentTextIcon' as IconName,
  }] : []),
  ...(checkPageAccess(userTags, ['support', 'support-templates'], 'view') ? [
    (() => {
      const resolveName = (name?: string | Record<string, string>): string | undefined => {
        if (!name) return undefined;
        if (typeof name === 'string') return name;
        return name.en;
      };
      const defaultChannel = loggedInConfig?.comms?.default;
      const showDefault = !!defaultChannel && canAccessSupportChannel(defaultChannel, userTags);
      const otherChannels = (loggedInConfig?.comms?.otherChannels || [])
        .filter(ch => canAccessSupportChannel(ch, userTags));
      const items = [
        ...(showDefault ? [{ name: resolveName(defaultChannel?.supportDashboard?.name) || L.admin_panel.nav.support, href: '/admin-panel/support/channel/default' }] : []),
        ...otherChannels.map(ch => ({ name: resolveName(ch.supportDashboard?.name) || ch.channelName, href: `/admin-panel/support/channel/${encodeURIComponent(ch.channelName)}` })),
      ];

      if (loggedInConfig?.comms?.supportTemplates && !loggedInConfig.comms.supportTemplates.disabled && checkPageAccess(userTags, 'support-templates', 'view')) {
        items.push({
          name: L.support.templates.page_title,
          href: '/admin-panel/support/support-templates',
        });
      }
      
      if (items.length === 0) return null;
      return {
        name: L.admin_panel.nav.support,
        iconName: 'ChatBubbleLeftRightIcon' as IconName,
        items
      } as NavItem;
    })()
  ].filter(Boolean) as NavItem[] : []),
  {
    name: L.admin_panel.nav.documentation,
    href: 'https://community.aidkit.cloud/partner-resources/admin-panel',
    iconName: 'BookOpenIcon',
    isExternal: true
  },
];

export type FlattenedNavCommand = {
  id: string;
  label: string;
  href: string;
  iconName?: IconName;
  description?: string;
  isExternal?: boolean;
  priority: number;
  searchTerms?: string[];
};

/** Maps leaf nav hrefs to keys under `command_palette.search_terms.navigation`. */
const NAV_HREF_SEARCH_TERM_KEYS: Record<string, string> = {
  '/admin-panel/home': 'overview',
  '/admin-panel/applicants/all': 'applicants',
  '/admin-panel/applicants/applicant-groups': 'applicant_groups',
  '/admin-panel/applicants/applicant-search': 'applicant_search',
  '/admin-panel/dashboards': 'dashboards',
  '/admin-panel/reports': 'reports',
  '/admin-panel/reviews/queues': 'review_queues',
  '/admin-panel/payments': 'accounting',
  '/admin-panel/balances': 'accounts',
  '/admin-panel/transactions': 'transactions',
  '/admin-panel/invoices': 'invoices',
  '/admin-panel/notifications': 'notifications',
  '/admin-panel/user-management/users': 'user_management',
  '/admin-panel/user-management/user-groups': 'user_groups',
  '/admin-panel/user-management/organizations': 'organizations',
  '/admin-panel/settings/manage-fields': 'manage_fields',
  '/admin-panel/audit-logs': 'audit_logs',
  'https://community.aidkit.cloud/partner-resources/admin-panel': 'documentation',
};

/**
 * Higher priority = shown earlier / included in the empty-search palette (>= 5).
 * Values match the pre-flatten AdminCommandPalette navigation ordering.
 */
const NAV_HREF_PRIORITIES: Record<string, number> = {
  '/admin-panel/home': 10,
  '/admin-panel/applicants/all': 10,
  '/admin-panel/applicants/applicant-groups': 7,
  '/admin-panel/applicants/applicant-search': 7,
  '/admin-panel/dashboards': 9,
  '/admin-panel/reports': 8,
  '/admin-panel/reviews/my-reviews': 8,
  '/admin-panel/reviews/queues': 8,
  '/admin-panel/payments': 7,
  '/admin-panel/balances': 6,
  '/admin-panel/transactions': 6,
  '/admin-panel/invoices': 6,
  '/admin-panel/notifications': 6,
  '/admin-panel/user-management/users': 5,
  '/admin-panel/user-management/user-groups': 5,
  '/admin-panel/user-management/organizations': 5,
  '/admin-panel/settings/manage-fields': 4,
  '/admin-panel/settings/support-templates': 4,
  '/admin-panel/audit-logs': 4,
  'https://community.aidkit.cloud/partner-resources/admin-panel': 3,
};

const DEFAULT_NAV_PRIORITY = 4;

function resolveNavLabel(name: string | { en: string; [key: string]: string | undefined }, lang?: string): string {
  if (typeof name === 'string') return name;
  return (lang && name[lang]) || name.en || '';
}

function resolveNavPriority(href: string): number {
  if (NAV_HREF_PRIORITIES[href] !== undefined) return NAV_HREF_PRIORITIES[href];
  // Support channels / templates: keep out of the default empty-search list
  if (href.startsWith('/admin-panel/support/')) return 3;
  return DEFAULT_NAV_PRIORITY;
}

function resolveNavSearchTerms(
  href: string,
  searchTermsNav?: Record<string, string | undefined>
): string[] | undefined {
  const key =
    NAV_HREF_SEARCH_TERM_KEYS[href] ??
    (href.startsWith('/admin-panel/support/') ? 'support' : undefined);
  if (!key) return undefined;
  const raw = searchTermsNav?.[key];
  if (!raw) return undefined;
  const terms = raw.split(',').map((t) => t.trim()).filter(Boolean);
  return terms.length > 0 ? terms : undefined;
}

/** Flatten sidebar nav into leaf commands for the admin command palette. */
export const flattenNavItemsForCommandPalette = (
  navItems: NavItem[],
  lang?: string,
  searchTermsNav?: Record<string, string | undefined>
): FlattenedNavCommand[] => {
  const commands: FlattenedNavCommand[] = [];

  for (const item of navItems) {
    const parentLabel = resolveNavLabel(item.name, lang);

    if (item.items && item.items.length > 0) {
      for (const child of item.items) {
        commands.push({
          id: `nav-${child.href}`,
          label: resolveNavLabel(child.name, lang),
          href: child.href,
          iconName: child.iconName ?? item.iconName,
          description: parentLabel,
          isExternal: item.isExternal,
          priority: resolveNavPriority(child.href),
          searchTerms: resolveNavSearchTerms(child.href, searchTermsNav),
        });
      }
      continue;
    }

    if (item.href) {
      commands.push({
        id: `nav-${item.href}`,
        label: parentLabel,
        href: item.href,
        iconName: item.iconName,
        isExternal: item.isExternal,
        priority: resolveNavPriority(item.href),
        searchTerms: resolveNavSearchTerms(item.href, searchTermsNav),
      });
    }
  }

  return commands;
};

// Helper function to generate parent routes that need redirects
export const getParentRedirectRoutes = (navItems: NavItem[]): Array<{ from: string; to: string }> => {
  const redirects: Array<{ from: string; to: string }> = [];

  navItems.forEach(item => {
    // If item has subitems but no href, we need to create a redirect
    if (item.items && item.items.length > 0 && !item.href) {
      // Derive the parent path from the first child's href
      const firstChildHref = item.items[0].href;
      const parentPath = firstChildHref.substring(0, firstChildHref.lastIndexOf('/'));

      if (parentPath && firstChildHref) {
        redirects.push({
          from: parentPath,
          to: firstChildHref
        });
      }
    }
  });

  return redirects;
};
